import { useMemo } from 'react';
import { Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { useAuthStore } from '@/stores/auth.js';
import { fetchReportMultiStore } from '@/lib/api/reports.js';
import { exportToCsv } from '@/lib/csv-export.js';
import { formatCurrency } from '@/lib/format.js';
import { useReportQueryParams } from '@/pages/reports/report-filters-context.js';

const LINE_COLORS = ['#6366f1', '#22c55e', '#f59e0b', '#ec4899', '#0ea5e9', '#a855f7', '#64748b', '#14b8a6'];

export function MultiStoreReportPage() {
  const user = useAuthStore((s) => s.user);
  const elevated = ['tenant_admin', 'platform_admin'].includes(user?.role ?? '');
  const qBuilt = useReportQueryParams();

  const q = useQuery({
    queryKey: ['reports-multi-store', qBuilt],
    queryFn: () => fetchReportMultiStore(qBuilt),
    enabled: elevated,
  });

  const d = q.data;

  const storeRows = useMemo(() => {
    if (!d?.stores?.length) return [];
    return d.stores.map((s) => ({
      storeName: s.storeName,
      revenue: s.revenue,
      tickets: s.tickets,
      avgTicketValue: s.avgTicketValue,
      openTickets: s.openTickets,
      percentOfRevenue: `${s.percentOfRevenue}%`,
      lowStockCount: s.lowStockCount,
      cashDrawerStatus: s.cashDrawerStatus,
    }));
  }, [d]);

  const chartData = useMemo(() => {
    if (!d?.chart?.length || !d.stores?.length) return [];
    return d.chart.map((row) => {
      const flat: Record<string, string | number> = { date: row.date };
      for (const st of d.stores) {
        flat[st.storeId] = Number(row.stores[st.storeId] ?? '0');
      }
      return flat;
    });
  }, [d]);

  const csvTable = () => {
    if (!d) return;
    exportToCsv(storeRows as unknown as Record<string, unknown>[], 'multi-store-summary.csv');
  };

  const csvSeries = () => {
    if (!d?.chart) return;
    exportToCsv(d.chart as unknown as Record<string, unknown>[], 'multi-store-daily.csv');
  };

  if (!elevated) return <Navigate to="/reports" replace />;

  if (q.isLoading) return <div className="rounded-2xl bg-white p-8 text-slate-500 shadow-sm">Loading multi-store…</div>;
  if (q.isError || !d) return <div className="rounded-2xl border border-red-100 bg-red-50 p-8 text-red-800">Could not load multi-store report.</div>;

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap justify-end gap-2">
        <button type="button" className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-semibold hover:bg-slate-50" onClick={csvTable}>
          Export summary CSV
        </button>
        <button type="button" className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-semibold hover:bg-slate-50" onClick={csvSeries}>
          Export daily CSV
        </button>
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-xs font-medium text-slate-500">Total revenue</p>
          <p className="mt-1 text-2xl font-bold text-slate-950">{formatCurrency(d.totalRevenue)}</p>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-xs font-medium text-slate-500">Tickets closed</p>
          <p className="mt-1 text-2xl font-bold text-slate-950">{d.totalTickets}</p>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-xs font-medium text-slate-500">Stores</p>
          <p className="mt-1 text-2xl font-bold text-slate-950">{d.stores.length}</p>
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <h3 className="text-sm font-bold text-slate-950">Revenue by store (daily)</h3>
        <div className="mt-4 h-96">
          {chartData.length && d.stores.length ? (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} minTickGap={24} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v: number) => formatCurrency(v)} />
                <Legend />
                {d.stores.map((st, i) => (
                  <Line
                    key={st.storeId}
                    type="monotone"
                    dataKey={st.storeId}
                    name={st.storeName}
                    stroke={LINE_COLORS[i % LINE_COLORS.length]}
                    dot={false}
                    strokeWidth={2}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-sm text-slate-500">No chart data for this range.</p>
          )}
        </div>
      </div>

      <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
        <table className="min-w-full text-left text-sm">
          <thead className="border-b border-slate-200 bg-slate-50 text-xs font-semibold uppercase text-slate-600">
            <tr>
              <th className="px-4 py-3">Store</th>
              <th className="px-4 py-3">Revenue</th>
              <th className="px-4 py-3">Tickets</th>
              <th className="px-4 py-3">Avg ticket</th>
              <th className="px-4 py-3">Open</th>
              <th className="px-4 py-3">% rev</th>
              <th className="px-4 py-3">Low stock</th>
              <th className="px-4 py-3">Drawer</th>
            </tr>
          </thead>
          <tbody>
            {d.stores.map((s) => (
              <tr key={s.storeId} className="border-b border-slate-100">
                <td className="px-4 py-2 font-medium">{s.storeName}</td>
                <td className="px-4 py-2">{formatCurrency(s.revenue)}</td>
                <td className="px-4 py-2">{s.tickets}</td>
                <td className="px-4 py-2">{formatCurrency(s.avgTicketValue)}</td>
                <td className="px-4 py-2">{s.openTickets}</td>
                <td className="px-4 py-2">{s.percentOfRevenue}%</td>
                <td className="px-4 py-2">{s.lowStockCount}</td>
                <td className="px-4 py-2 text-xs capitalize">{s.cashDrawerStatus.replace(/_/g, ' ')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
